import {
  DAY_NAMES,
  formatSlotForDisplay,
  normalizeSlot,
  timeStringToMinutes,
} from "../../../utils/availability";
import { describeTimeZone } from "../../../utils/zonedTime";
import SectionHead, { sectionText } from "./SectionHead";

// Usual hours on a Custom page: the pro's weekly hours from Availability,
// Monday to Sunday. Blocked dates and one-off changes aren't in the public
// payload, so this is labelled as the usual week, never as a promise for a
// given day. A week with no hours at all has no section.

// Monday first, as clients read a week.
const WEEK = [1, 2, 3, 4, 5, 6, 0].map((index) => DAY_NAMES[index]);

const byStart = (a, b) =>
  (timeStringToMinutes(a.start) ?? 0) - (timeStringToMinutes(b.start) ?? 0);

export const buildUsualHours = (availability) => {
  const entries = Array.isArray(availability) ? availability : [];
  return WEEK.map((day) => {
    const entry = entries.find(
      (candidate) =>
        typeof candidate?.day === "string" && candidate.day.toLowerCase() === day.toLowerCase(),
    );
    const slots = (Array.isArray(entry?.slots) ? entry.slots : [])
      .map(normalizeSlot)
      .filter(Boolean)
      .sort(byStart);
    // "9:00 AM - 5:00 PM", with the design's en dash between the two times.
    const times = slots.map((slot) => formatSlotForDisplay(slot).replace(" - ", " – "));
    return { day, times };
  });
};

// "Central Time" for America/Chicago. Empty when there's no zone or the
// browser can't name it, and then the sentence just leaves it out. It lives in
// utils/zonedTime, which the dashboard shares.
export { describeTimeZone };

const ProfileHoursSection = ({ sectionId = "hours", content, availability, timeZone }) => {
  const days = buildUsualHours(availability);
  if (!days.some((entry) => entry.times.length > 0)) return null;

  const heading = sectionText(content?.heading) || "Usual hours";
  const zoneName = describeTimeZone(timeZone);
  const lede = `${zoneName ? `${zoneName}. ` : ""}Pick a service to see the times you can book.`;
  const headingId = `profile-section-${sectionId}-heading`;

  return (
    <section className="profile-block profile-hours" aria-labelledby={headingId}>
      <SectionHead id={headingId} heading={heading} lede={lede} />
      <dl className="profile-block__card profile-hours__list">
        {days.map(({ day, times }) => (
          <div className="profile-hours__row" key={day}>
            <dt className="profile-hours__day">{day}</dt>
            {times.length > 0 ? (
              <dd className="profile-hours__times">
                {times.map((time) => (
                  <span key={time}>{time}</span>
                ))}
              </dd>
            ) : (
              <dd className="profile-hours__times profile-hours__times--closed">Closed</dd>
            )}
          </div>
        ))}
      </dl>
    </section>
  );
};

export default ProfileHoursSection;
